import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  webFormatCurrency,
  RootState,
  createEppPlanRequest,
  createZeroPercentEppRequest,
  resetDisclosureSummary,
} from '@rb-serve-app/core';
import ProgressBar from '../../components/ProgressBar';
import Breadcrumbs from '../../components/BreadCrumbs';
import Button from '../../components/Button';
import { Button as FormButton } from '../../components/Form';
import DisclosureSummary from '../../components/DisclosureSummary';
import moment from 'moment';
import { generateEppDisclosurePdf } from '../../utility/pdfGenerator';
import { useRecaptchaToken } from '@rb-serve-app/core';
import { EventType, useAnalytics } from '../../ui/Analytics';

interface SchumerBoxRow {
  title: string;
  value: string;
  description: string;
}

const ReviewEppPlan: React.FC = () => {
  const { trackEvent } = useAnalytics();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const getRecaptchaToken = useRecaptchaToken();

  // Manual toggle for progress bar visibility
  const showProgressBar = false; // Change to true/false to show/hide progress bar
 const { accountDetails } = useSelector((state: RootState) => state.user);
  const { transaction, selectedOption, accountId, customerId } =
    location.state || {};
  const [planName, setPlanName] = useState('');
  const [hasScrolledDisclosure, setHasScrolledDisclosure] = useState(false);
  const [showCheckboxTooltip, setShowCheckboxTooltip] = useState(false);
  const [showSubmitTooltip, setShowSubmitTooltip] = useState(false);
  const [isConsentChecked, setIsConsentChecked] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(2);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { contentfulData } = useSelector(
    (state: { contentful: any }) => state.contentful
  );

  const { success, error, loading } = useSelector(
    (state: RootState) => state.disclosureSummary
  );

  const { entries, assets } = contentfulData || {};
  const eppContentful = entries?.rb_epp || {};
  const rbWebConfig = eppContentful?.eppWebConfig || {};
  const schumerBoxContentful = eppContentful?.schumerBoxData || [];
  const lang = localStorage.getItem('language') || 'en-US';

  // Determine which popup content to use based on EPP type
  const getPopupContent = () => {
    const isZeroEpp = transaction?.eppType === '0%';
    const popupKey = isZeroEpp ? 'ZeroEpp' : 'nonZero';
    return rbWebConfig?.reviewEppPlan?.submitPopupBox?.[popupKey] || {};
  };

  const stepLabels = [
    'Select Equal Payment Plan',
    'Create & Review Your Plan',
    'Request Submitted',
  ];

  const formatDate = (dateString: string) =>
    moment(dateString).format('MMM DD, YYYY');

  // Calculate values for schumer box
  const monthlyPayment = selectedOption?.paymentAmount?.value
    ? webFormatCurrency(Number(selectedOption.paymentAmount.value), lang)
    : '$xxx.xx';
  const numberOfPayments = selectedOption?.numberOfPayments || 'x';
  const annualRate = selectedOption?.rate || '0.00';
  const oneTimeFee =
    selectedOption?.optionType === 'non0%' || transaction?.eppType === 'non0%'
      ? '3%'
      : lang === 'fr'
        ? '0 $'
        : '$0';
  const netCapital = transaction?.amount?.value
    ? webFormatCurrency(Number(transaction.amount.value), lang)
    : '$x';

  // Build schumer box data using contentful
  const schumerBoxData: SchumerBoxRow[] = [
    {
      title: schumerBoxContentful[0]?.title || 'Monthly Payment',
      value: monthlyPayment,
      description:
        transaction?.eppType === 'non0%'
          ? schumerBoxContentful[0]?.descriptions?.non_zero ||
            schumerBoxContentful[0]?.description
          : schumerBoxContentful[0]?.descriptions?.default ||
            schumerBoxContentful[0]?.description ||
            'This is the monthly payment amount that you have split the transaction into with this Equal Payment Plan. It includes principal plus interest on the plan (as at the end of the term) divided by the number of payments. It may take up to 10 days for your Equal Payment Plan to be posted to your Account.',
    },
    {
      title: schumerBoxContentful[1]?.title || 'Number of Payments',
      value: `${schumerBoxContentful[1]?.value?.replace('%s', numberOfPayments) || 'payments'}`,
      description:
        transaction?.eppType === 'non0%'
          ? schumerBoxContentful[1]?.descriptions?.non_zero ||
            schumerBoxContentful[1]?.description
          : schumerBoxContentful[1]?.descriptions?.default ||
            schumerBoxContentful[1]?.description ||
            'Your number of monthly payments is based on the Equal Payment Plan term you selected. Monthly payments begin from the first Statement issued after the Equal Payment Plan is processed.',
    },
    {
      title: schumerBoxContentful[2]?.title || 'Annual Interest Rate',
      value: `${annualRate}%`,
      description:
        transaction?.eppType === 'non0%'
          ? schumerBoxContentful[2]?.descriptions?.non_zero ||
            schumerBoxContentful[2]?.description
          : schumerBoxContentful[2]?.descriptions?.default ||
            schumerBoxContentful[2]?.description ||
            'This is the annual interest rate on the Equal Payment Plan you chose. Interest will be calculated as set out in the Rogers Bank Cardholder Agreement. Interest may change subject to the Equal Payment Plan Terms & Conditions.',
    },
    {
      title: schumerBoxContentful[3]?.title || 'One-time fee',
      value: oneTimeFee,
      description:
        transaction?.eppType === 'non0%'
          ? schumerBoxContentful[3]?.descriptions?.non_zero ||
            schumerBoxContentful[3]?.description
          : schumerBoxContentful[3]?.descriptions?.default ||
            schumerBoxContentful[3]?.description ||
            'One-time set up fee will be charged at the time the transaction is converted to an Equal Payment Plan. This fee is not applicable to Quebec residents.',
    },
    {
      title:
        transaction?.eppType === 'non0%'
          ? eppContentful?.eppProcessingTime || 'Processing Time'
          : schumerBoxContentful[4]?.title ||
            'Net Capital (Applicable to Quebec residents only)',
      value:
        transaction?.eppType === 'non0%'
          ? eppContentful?.eppProcessingTimeDays || '2-3 business days'
          : netCapital,
      description:
        transaction?.eppType === 'non0%'
          ? schumerBoxContentful[4]?.descriptions?.non_zero ||
            "After your Equal Payment Plan has been processed, you can view details by:\n1. selecting the 'Manage Equal Payment Plans' option in the main menu of your Online Banking Account; or\n2. reviewing the 'Equal Payment Plan Summary' on your Statement."
          : schumerBoxContentful[4]?.descriptions?.default ||
            schumerBoxContentful[4]?.description ||
            'This is your total Purchase amount plus interest, if any, over the term of this Equal Payment Plan. Amount is estimated as of the date the eligible Purchase is converted and may change subject to the Equal Payment Plan Terms & Conditions.',
    },
  ];

  const handleBack = () => {
    trackClickEvent('create and review your plan|Back');
    navigate('/select-equal-payment-plan', {
      state: {
        transaction: transaction,
        accountId: accountId,
        customerId: customerId,
      },
      replace: true,
    });
  };

  const handleCancel = () => {
    trackClickEvent('create and review your plan|Cancel');
    navigate('/transactions', { replace: true });
  };

  const handleSubmitRequest = async () => {
    trackClickEvent('create and review your plan|Submit');
    if (!hasScrolledDisclosure || !isConsentChecked) {
      return;
    }

    // Check if already submitting to prevent double-submission
    if (loading || isSubmitting) {
      console.log('Submission already in progress, ignoring duplicate click');
      return;
    }

    // Set submission flags immediately to block any further attempts
    setIsSubmitting(true);
    console.log('Starting EPP submission at', new Date().toISOString());

    // For 0% EPP, provide a default plan name if none is entered
    const finalPlanName =
      transaction?.eppType === '0%' && !planName.trim()
        ? `${selectedOption?.numberOfPayments || '36'} Month 0% Equal Payment Plan`
        : planName;

    try {
      if (transaction?.eppType === 'non0%') {
        const token = await getRecaptchaToken('non_zeropercent_epp');
        // Handle non-zero percent EPP
        console.log('Submitting non-zero percent EPP');

        // Get the activity ID from the transaction object
        const activityId = transaction?.activityId;
        if (!activityId) {
          console.error('Missing activityId for non-zero percent EPP');
          setIsSubmitting(false);
          setShowErrorModal(true);
          return;
        }

        // Get the option code from the selected option
        const optionCode = selectedOption?.optionCode;
        if (!optionCode) {
          console.error('Missing optionCode for non-zero percent EPP');
          setIsSubmitting(false);
          setShowErrorModal(true);
          return;
        }

        // Prepare request body for non-zero percent EPP
        const requestBody = {
          activityId,
          optionCode,
          planName: finalPlanName || 'Equal Payment Plan',
          recaptchaToken: token,
        };

        console.log(
          'Non-zero percent EPP request body:',
          JSON.stringify(requestBody)
        );

        // Dispatch action to create EPP plan
        dispatch(
          createEppPlanRequest({
            accountId,
            customerId,
            requestBody,
          })
        );
      } else {
        // Handle zero percent EPP
        console.log('Submitting zero percent EPP');

        // Get account details for zero percent EPP
        const ban = accountId ? accountId.replace(/^0+/, '') : '';
        const token = await getRecaptchaToken('zeropercent_epp');
        // Prepare request body for zero percent EPP
        const zeroPercentRequestBody = {
          first_name: accountDetails?.customer?.firstName || '',
          last_name: accountDetails?.customer?.lastName || '',
          email: '',
          phone: '',
          account_id: accountId || '',
          customer_id: customerId || '',
          reference_number: transaction?.referenceNumber || '',
          last_four_cc: transaction?.cardNumber?.slice(-4) || '',
          ban: ban,
          purchase_date: transaction?.date || '',
          purchase_amount: transaction?.amount?.value || '',
          primary_cardholder: '',
          resident_of_quebec: '',
          province: '',
          type: 'EPP_Web',
          offerTerm: `${selectedOption?.numberOfPayments || '36'}`,
          imei: [],
          language: lang === 'fr-CA' ? 'FR' : 'EN',
          dealer_code: '',
          rep_email: '',
          recaptchaToken: token,
        };

        console.log(
          'Zero percent EPP request body:',
          JSON.stringify(zeroPercentRequestBody)
        );

        // Dispatch action to create zero percent EPP plan
        dispatch(
          createZeroPercentEppRequest({
            requestBody: zeroPercentRequestBody,
          })
        );
      }
    } catch (error) {
      console.error('Error preparing EPP submission:', error);
      setIsSubmitting(false);
      setShowErrorModal(true);
    }
  };

  const handleDownloadPlan = async () => {
    trackClickEvent('create and review your plan|Print & save');
    try {
      const titleText =
        eppContentful?.eppPdfTitle || 'Equal Payment Plan Summary';
      const timestamp = moment().format('YYYYMMDDHHmmss');
      const filename = `${eppContentful?.eppPdfTitle || 'EPP_Summary'}_${timestamp}.pdf`;

      await generateEppDisclosurePdf({
        schumerBoxData,
        titleText,
        filename,
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Unable to generate PDF. Please try again later.');
    }
  };

  const handleScrollComplete = (isComplete: boolean) => {
    setHasScrolledDisclosure(isComplete);
  };

  const handleCheckboxChange = () => {
    trackClickEvent(
      'create and review your plan|Equal Payment Plan Terms and conditions checkbox'
    );
    if (hasScrolledDisclosure) {
      setIsConsentChecked(!isConsentChecked);
    }
  };

  const handleCloseSuccessModal = () => {
    trackClickEvent('create and review your plan|Close');
    setShowSuccessModal(false);

    // Reset Redux state
    dispatch(resetDisclosureSummary());

    navigate('/transactions', { replace: true });
  };

  const handleCloseErrorModal = () => {
    trackClickEvent('create and review your plan|Close');
    setShowErrorModal(false);

    // Reset Redux state
    dispatch(resetDisclosureSummary());

    // Navigate back to transactions page
    navigate('/transactions', { replace: true });
  };

  // Adobe tags
  const trackPageView = (data: any) => {
    trackEvent(
      EventType.PAGE_VIEW,
      ['create and review your plan', 'enroll equal payment plans'],
      window.location.href,
      undefined,
      false,
      {},
      data
    );
  };

  const errorData = (
    category: string,
    type: string,
    code: string,
    api: string,
    message: string
  ) => {
    let errorObject = {};
    errorObject = {
      category: category,
      type: type,
      code: code,
      API: api,
      field: '',
      message: message,
    };
    trackPageView(errorObject);
  };

  useEffect(() => {
    trackPageView('');
    if (success && success != 200) {
      errorData(
        'eppSubmit',
        'eppSubmit',
        success?.toString(),
        'disclosureSummary',
        'Data Not Found'
      );
    }
  }, []);

  const trackClickEvent = (name: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      ['create and review your plan', 'enroll equal payment plans'],
      window.location.href,
      `${name}`
    );
  };

  // Disable scroll when modal is open
  useEffect(() => {
    if (showSuccessModal || showErrorModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup function to restore scroll on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showSuccessModal, showErrorModal]);

  // Handle success/error responses from Redux
  useEffect(() => {
    if (success && isSubmitting) {
      setShowSuccessModal(true);
      setShowErrorModal(false);
      setIsSubmitting(false);
      setCurrentStep(3);
    } else if (error && isSubmitting) {
      setShowErrorModal(true);
      setShowSuccessModal(false);
      setIsSubmitting(false);
    }
  }, [success, error, isSubmitting]);

  // Reset disclosure summary state on component mount
  useEffect(() => {
    dispatch(resetDisclosureSummary());
    return () => {
      dispatch(resetDisclosureSummary());
    };
  }, [dispatch]);

  const isSubmitEnabled =
    hasScrolledDisclosure &&
    isConsentChecked &&
    (transaction?.eppType === '0%' ||
      (planName.trim().length > 0 && planName.length < 40));

  if (!transaction || !selectedOption) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[#1F1F1F]">No transaction or plan data available</p>
      </div>
    );
  }

  return (
    <div className="bg-bg-body py-4 md:px-8 px-4">
      <Breadcrumbs
        items={[
          { name: 'Home', href: '/home' },
          {
            name: rbWebConfig?.reviewEppPlan?.transactionHistoryBreadCrumb,
            href: '/transactions',
          },
          {
            name: rbWebConfig?.reviewEppPlan?.equalPaymentPlanBreadCrumb,
            href: '/select-equal-payment-plan',
          },
        ]}
      />

      <div className="w-full rounded-[15px] bg-white min-h-[70vh] flex-1 flex-col overflow-y-scroll scrollbar-hide md:p-6 p-2">
        {/* Header with Download Button */}
        <div className="flex flex-row justify-between items-center mb-6">
          <h1
            aria-label={rbWebConfig?.reviewEppPlan?.title}
            className="text-[#1F1F1F] font-['Ted_Next'] text-[18px] md:text-[22px] font-[650] leading-[24px] md:leading-[28px] tracking-[0.077px]"
          >
            {rbWebConfig?.reviewEppPlan?.title}
          </h1>

          {/* Desktop Download Button */}
          <button
            aria-label={rbWebConfig?.reviewEppPlan?.printAndSaveCta}
            onClick={handleDownloadPlan}
            className="hidden md:inline-flex items-center justify-center gap-[8px] px-[8px] py-[8px] border border-[#DA291C] bg-white rounded text-[#DA291C] font-['Ted_Next'] text-[16px] font-[650] leading-[20px] tracking-[-0.051px]"
          >
            {rbWebConfig?.reviewEppPlan?.printAndSaveCta}
            {assets?.download_icon_print_save && (
              <img
                src={assets.download_icon_print_save}
                alt="Download"
                className="w-5 h-5"
              />
            )}
          </button>

          {/* Mobile Download Icon */}
          {assets?.download_icon_print_save && (
            <button onClick={handleDownloadPlan} className="md:hidden p-2">
              <img
                src={assets.download_icon_print_save}
                alt="Download"
                className="w-6 h-6"
              />
            </button>
          )}
        </div>

        {/* Progress Bar */}
        {showProgressBar && (
          <ProgressBar
            currentStep={currentStep}
            totalSteps={3}
            stepLabels={stepLabels}
            assets={assets}
          />
        )}

        {/* Name Your Plan - Only show for non-0% EPP */}
        {transaction?.eppType !== '0%' && (
          <div className="mb-6 py-2 ">
            <label
              aria-label={rbWebConfig?.reviewEppPlan?.nameYourPlanLabel}
              htmlFor="planName"
              className="block text-[#1F1F1F] font-['Ted_Next'] text-[16px] font-medium leading-[20px] tracking-[-0.051px] mb-2"
            >
              {rbWebConfig?.reviewEppPlan?.nameYourPlanLabel}
              <span className="text-[#DA291C]">*</span>
            </label>
            <div className="relative">
              <textarea
                id="planName"
                value={planName}
                onChange={(e) => setPlanName(e.target.value)}
                placeholder={
                  rbWebConfig?.reviewEppPlan?.enterPlanNamePlaceholder
                }
                maxLength={50}
                rows={1}
                className={`w-full max-w-md px-4 py-3 border rounded focus:outline-none focus:ring-2 focus:border-transparent resize-none overflow-hidden ${
                  planName.length > 40
                    ? 'border-red-500 focus:ring-red-500'
                    : 'border-gray-300 focus:ring-[#DA291C]'
                }`}
                style={{
                  minHeight: '48px',
                  lineHeight: '1.2',
                  wordWrap: 'break-word',
                }}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = 'auto';
                  target.style.height =
                    Math.max(48, target.scrollHeight) + 'px';
                }}
              />
              <div className="flex justify-between items-center mt-1">
                <div>
                  {planName.length >= 40 && (
                    <p
                      className="text-red-500 text-sm"
                      aria-label={rbWebConfig?.reviewEppPlan?.panNameError}
                    >
                      {rbWebConfig?.reviewEppPlan?.panNameError}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transaction and Plan Details */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Transaction Details */}
          <div className="rounded-[10px] bg-[#FAFAFA] p-6">
            <h3
              aria-label={rbWebConfig?.reviewEppPlan?.transactionDetailsLabel}
              className="text-[#1F1F1F] text-lg font-semibold mb-4"
            >
              {rbWebConfig?.reviewEppPlan?.transactionDetailsLabel}
            </h3>
            <div className="h-[1px] bg-[#E9E9E9] flex-shrink-0 mb-8"></div>

            {/* Desktop Layout */}
            <div className="hidden md:block space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span
                  aria-label={transaction?.merchant?.name}
                  className="text-[#666666]"
                >
                  {transaction?.merchant?.name}
                </span>
                <span
                  aria-label={rbWebConfig?.reviewEppPlan?.transactionDateLabel}
                  className="text-[#1F1F1F] text-right"
                >
                  {rbWebConfig?.reviewEppPlan?.transactionDateLabel}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span
                  aria-label={transaction.amount.value}
                  className="text-[#1F1F1F] font-medium"
                >
                  {transaction?.amount?.value
                    ? webFormatCurrency(Number(transaction.amount.value), lang)
                    : '$1050.00'}
                </span>
                <span
                  aria-label={transaction.date}
                  className="text-[#1F1F1F] font-medium text-right"
                >
                  {transaction?.date
                    ? formatDate(transaction.date)
                    : 'Jul 18, 2024'}
                </span>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-4 text-sm">
              <div>
                <p
                  aria-label={transaction?.merchant?.name}
                  className="text-[#666666]"
                >
                  {transaction?.merchant?.name || 'Rogers #13'}
                </p>
                <p
                  aria-label={transaction.amount.value}
                  className="text-[#1F1F1F] font-medium"
                >
                  {transaction?.amount?.value
                    ? webFormatCurrency(Number(transaction.amount.value), lang)
                    : '$1050.00'}
                </p>
              </div>
              <div>
                <p aria-label="Transaction Date" className="text-[#666666]">
                  Transaction Date
                </p>
                <p
                  aria-label={transaction.date}
                  className="text-[#1F1F1F] font-medium"
                >
                  {transaction?.date
                    ? formatDate(transaction.date)
                    : 'Jul 18, 2024'}
                </p>
              </div>
            </div>
          </div>

          {/* Plan Details */}
          <div className="rounded-[10px] bg-[#FAFAFA] p-6">
            <h3
              aria-label={rbWebConfig?.reviewEppPlan?.planDetailsLabel}
              className="text-[#1F1F1F] text-lg font-semibold mb-4"
            >
              {rbWebConfig?.reviewEppPlan?.planDetailsLabel}
            </h3>
            <div className="h-[1px] bg-[#E9E9E9] flex-shrink-0 mb-8"></div>

            {/* Desktop Layout */}
            <div className="hidden md:block space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <span
                  className="text-[#666666]"
                  aria-label={
                    rbWebConfig?.reviewEppPlan?.monthlyPaymentTermLabel
                  }
                >
                  {rbWebConfig?.reviewEppPlan?.monthlyPaymentTermLabel}
                </span>
                <span
                  className="text-[#666666] text-right"
                  aria-label={rbWebConfig?.reviewEppPlan?.monthlyPaymentLabel}
                >
                  {rbWebConfig?.reviewEppPlan?.monthlyPaymentLabel}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <span className="text-[#1F1F1F] font-medium">
                  {rbWebConfig?.reviewEppPlan?.monthsLabel?.replace(
                    '%s',
                    selectedOption?.numberOfPayments || ''
                  ) || `${selectedOption?.numberOfPayments || ''} `}
                </span>
                <span
                  aria-label={selectedOption.paymentAmount.value}
                  className="text-[#1F1F1F] font-medium text-right"
                >
                  {selectedOption?.paymentAmount?.value
                    ? webFormatCurrency(
                        Number(selectedOption.paymentAmount.value),
                        lang
                      )
                    : '$183.73'}
                </span>
              </div>
            </div>

            {/* Mobile Layout */}
            <div className="md:hidden space-y-4 text-sm">
              <div>
                <p
                  className="text-[#666666]"
                  aria-label={
                    rbWebConfig?.reviewEppPlan?.monthlyPaymentTermLabel
                  }
                >
                  {rbWebConfig?.reviewEppPlan?.monthlyPaymentTermLabel}
                </p>
                <p className="text-[#1F1F1F] font-medium">
                  {rbWebConfig?.reviewEppPlan?.monthsLabel?.replace(
                    '%s',
                    selectedOption?.numberOfPayments || ''
                  ) || `${selectedOption?.numberOfPayments || ''} months`}
                </p>
              </div>
              <div>
                <p
                  className="text-[#666666]"
                  aria-label={rbWebConfig?.reviewEppPlan?.monthlyPaymentLabel}
                >
                  {rbWebConfig?.reviewEppPlan?.monthlyPaymentLabel}
                </p>
                <p
                  className="text-[#1F1F1F] font-medium"
                  aria-label={selectedOption.paymentAmount.value}
                >
                  {selectedOption?.paymentAmount?.value
                    ? webFormatCurrency(
                        Number(selectedOption.paymentAmount.value),
                        lang
                      )
                    : '$183.73'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Separator line */}

        {/* Your consent is required */}
        <div className="mb-6">
          <h3
            className="text-[#1F1F1F] text-lg font-semibold mb-4"
            aria-label={rbWebConfig?.reviewEppPlan?.yourConsentIsRequiredLabel}
          >
            {rbWebConfig?.reviewEppPlan?.yourConsentIsRequiredLabel}
          </h3>
          <p
            className="text-[#1F1F1F] text-sm mb-4"
            aria-label={rbWebConfig?.reviewEppPlan?.disclosureInformationLabel}
          >
            {rbWebConfig?.reviewEppPlan?.disclosureInformationLabel}
          </p>
        </div>

        {/* Disclosure Summary and Consent Container */}
        <div className="rounded-[10px] border border-[#E9E9E9] bg-white p-6 mb-6">
          {/* Disclosure Summary */}
          <DisclosureSummary
            selectedOption={selectedOption}
            transaction={transaction}
            onScrollComplete={handleScrollComplete}
            schumerBoxData={schumerBoxData}
            schumerBoxLabel={rbWebConfig?.reviewEppPlan?.disclosureSummaryLabel}
            scrollToBottomLabel={
              rbWebConfig?.reviewEppPlan?.scrollToBottomLabel
            }
          />

          {/* Consent Checkbox */}
          <div className="mt-6">
            <div className="flex items-start">
              <div
                className="relative"
                onMouseEnter={() =>
                  !hasScrolledDisclosure && setShowCheckboxTooltip(true)
                }
                onMouseLeave={() => setShowCheckboxTooltip(false)}
              >
                <input
                  type="checkbox"
                  id="consent"
                  checked={isConsentChecked}
                  onChange={handleCheckboxChange}
                  disabled={!hasScrolledDisclosure}
                  className={`mr-3 mt-1 ${
                    !hasScrolledDisclosure
                      ? 'cursor-not-allowed opacity-50'
                      : 'cursor-pointer'
                  }`}
                />
                {showCheckboxTooltip && !hasScrolledDisclosure && (
                  <div
                    aria-label={rbWebConfig?.reviewEppPlan?.checkBoxHoverText}
                    className="absolute bottom-full left-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap"
                  >
                    {rbWebConfig?.reviewEppPlan?.checkBoxHoverText}
                  </div>
                )}
              </div>
              <label
                htmlFor="consent"
                className={`text-sm ${
                  !hasScrolledDisclosure
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-[#1F1F1F] cursor-pointer'
                }`}
              >
                {rbWebConfig?.reviewEppPlan?.tnc?.tncLable ||
                  'I have reviewed and agree to the Equal Payment Plan Summary and the'}{' '}
                <span
                  aria-label={rbWebConfig?.reviewEppPlan?.tnc?.tncClickableText}
                  className="text-[#DA291C] hover:underline cursor-pointer"
                  onClick={() => {
                    trackClickEvent(
                      'create and review your plan|Equal Payment Plan Terms and conditions document'
                    );
                    if (eppContentful?.eppTermsConditionsUrl) {
                      window.open(
                        eppContentful.eppTermsConditionsUrl,
                        '_blank'
                      );
                    }
                  }}
                >
                  {rbWebConfig?.reviewEppPlan?.tnc?.tncClickableText ||
                    'Equal Payment Plan Terms and Conditions'}
                </span>
              </label>
            </div>
          </div>
        </div>

        {/* Separator line */}
        <div className="h-[1px] bg-[#E9E9E9] flex-shrink-0 mb-6"></div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 sm:justify-between">
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              ariaLabel={rbWebConfig?.common?.cancelCta}
              variant="outline"
              onClick={handleCancel}
              className="border border-[#DA291C] text-[#DA291C] px-8 py-2 hover:bg-[#DA291C] hover:text-white transition-colors w-full sm:w-auto"
            >
              {rbWebConfig?.common?.cancelCta}
            </Button>
            <Button
              ariaLabel={rbWebConfig?.common?.backCta}
              variant="outline"
              onClick={handleBack}
              className="border border-[#DA291C] text-[#DA291C] px-8 py-2 hover:bg-[#DA291C] hover:text-white transition-colors w-full sm:w-auto"
            >
              {rbWebConfig?.common?.backCta}
            </Button>
          </div>
          <div
            className="relative w-full sm:w-auto"
            onMouseEnter={() => {
              if (!isSubmitEnabled) {
                setShowSubmitTooltip(true);
              }
            }}
            onMouseLeave={() => setShowSubmitTooltip(false)}
          >
            <div className="relative w-full sm:w-auto min-w-[140px]">
              <FormButton
                text={
                  loading || isSubmitting
                    ? ''
                    : rbWebConfig?.common?.submitRequestCta
                }
                disabled={!isSubmitEnabled || loading || isSubmitting}
                loader={loading || isSubmitting}
                onClick={handleSubmitRequest}
              />
              {(loading || isSubmitting) && (
                <div className="flex justify-center items-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="animate-spin ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6"></div>
                </div>
              )}
            </div>
            {showSubmitTooltip && !isSubmitEnabled && (
              <div className="absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-10 whitespace-nowrap">
                {!hasScrolledDisclosure
                  ? rbWebConfig?.reviewEppPlan?.scrollToBottomLabel
                  : !isConsentChecked
                    ? rbWebConfig?.reviewEppPlan?.checkTncToContinue
                    : transaction?.eppType !== '0%' && !planName.trim()
                      ? rbWebConfig?.reviewEppPlan?.enterAPlanNameToSubmit
                      : transaction?.eppType !== '0%' && planName.length >= 40
                        ? rbWebConfig?.reviewEppPlan?.panNameError
                        : 'Please complete all requirements to submit'}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
            {/* Success Icon */}
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center">
                <img
                  src={assets?.icon_success}
                  alt="Success"
                  className="w-16 h-16"
                />
              </div>
            </div>
            <h3
              aria-label={
                getPopupContent()?.success?.title || 'Request submitted'
              }
              className="text-xl font-semibold text-[#1F1F1F] mb-4"
            >
              {getPopupContent()?.success?.title || 'Request submitted'}
            </h3>

            <p
              aria-label={
                getPopupContent()?.success?.description ||
                'Your request to convert this purchase into an Equal Payment Plan has been received. Please allow up to 10 days for processing.\n\nYou will receive a confirmation email when the Equal Payment Plan has been posted to your account.'
              }
              className="text-[#1F1F1F] text-sm mb-6 leading-relaxed whitespace-pre-line"
            >
              {getPopupContent()?.success?.description ||
                'Your request to convert this purchase into an Equal Payment Plan has been received. Please allow up to 10 days for processing.\n\nYou will receive a confirmation email when the Equal Payment Plan has been posted to your account.'}
            </p>

            <Button
              ariaLabel={
                rbWebConfig?.reviewEppPlan?.submitPopupBox?.success?.cta
              }
              onClick={handleCloseSuccessModal}
              className="bg-[#DA291C] text-white px-8 py-3 hover:bg-red-700 transition-colors w-full"
            >
              {getPopupContent()?.success?.cta || 'OK'}
            </Button>
          </div>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 text-center">
            {/* Error Icon */}
            <h3
              aria-label={
                getPopupContent()?.error?.title || 'Submission Failed'
              }
              className="text-xl font-semibold text-[#1F1F1F] mb-4"
            >
              {getPopupContent()?.error?.title || 'Submission Failed'}
            </h3>

            <p
              aria-label={
                getPopupContent()?.error?.description ||
                (typeof error === 'string'
                  ? error
                  : 'There was an error submitting your request. Please try again later.')
              }
              className="text-[#1F1F1F] text-sm mb-6 leading-relaxed"
            >
              {getPopupContent()?.error?.description ||
                (typeof error === 'string'
                  ? error
                  : 'There was an error submitting your request. Please try again later.')}
            </p>

            <Button
              ariaLabel={rbWebConfig?.reviewEppPlan?.submitPopupBox?.error?.cta}
              onClick={handleCloseErrorModal}
              className="bg-[#DA291C] text-white px-8 py-3 hover:bg-red-700 transition-colors w-full"
            >
              {getPopupContent()?.error?.cta || 'OK'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewEppPlan;
